// CMA-OPT-68 Fix 1 + CMA-OPT-69 (USER ORDER 2026-08-13): the live progress
// surface for a CMA request.
//
// WHY THIS EXISTS: the grounded progress lines were received, normalized, and
// then rendered into the ThinkingPanel — whose body is collapsed for the whole
// run (#179) and whose header only surfaces `running` steps, while the lines
// arrived as `completed`. On top of that, the panel is not even mounted for
// specialty (CMA) responses. So every line was delivered and none was visible.
// This component is the always-visible surface: it renders each CMA stage as
// its own line, a spinner while that stage runs, a green check the moment that
// stage's OWN completion event lands.
//
// CONTRACT (user-approved):
// - A line appears when its stage STARTS, not when it completes.
// - The icon binds to the line's OWN completion event — never to "the next
//   line appeared" — so parallel stages stay honest.
// - The line's words never change between start and completion (no counts);
//   only the icon changes. The START event's text wins.
import { AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import type { NormalizedThinkingEvent } from './types';

const CMA_STEP_PREFIX = 'agentic_core.adk.cma.';

// CMA-OPT-72: the pre-route line. Every CMA stage key is decided only after the
// router has classified the request, measured live at T+3.16s, so a surface that
// matched the CMA prefix alone showed nothing for the first ~3.2s of a run. This
// key is emitted at control-plane entry (T+0.48s measured) and claims only that
// the request is being read, which is true on every route. It is an exact key,
// not a prefix, so no other pre-route step can leak into the answer area.
const PRE_ROUTE_STEP_KEY = 'agentic_core.adk.request_review';

function isProgressStepKey(stepKey: string): boolean {
  return stepKey === PRE_ROUTE_STEP_KEY || stepKey.startsWith(CMA_STEP_PREFIX);
}

interface CmaProgressLine {
  key: string;
  title: string;
  status: string;
  degraded: boolean;
}

interface CmaProgressLinesProps {
  events: NormalizedThinkingEvent[];
  isStreaming: boolean;
}

export function buildCmaProgressLines(events: NormalizedThinkingEvent[]): CmaProgressLine[] {
  const lines: CmaProgressLine[] = [];
  const indexByKey = new Map<string, number>();
  for (const event of events) {
    const stepKey = String((event.metadata as Record<string, unknown>)?.step_key ?? '');
    if (!isProgressStepKey(stepKey)) continue;
    const existing = indexByKey.get(stepKey);
    if (existing === undefined) {
      indexByKey.set(stepKey, lines.length);
      lines.push({
        key: stepKey,
        title: event.title,
        status: event.status,
        degraded: event.severity !== 'info',
      });
    } else {
      const line = lines[existing];
      // Later events flip the icon only; the START text stays as written.
      line.status = event.status;
      line.degraded = line.degraded || event.severity !== 'info';
    }
  }
  return lines;
}

export function CmaProgressLines({ events, isStreaming }: CmaProgressLinesProps) {
  if (!isStreaming) return null;
  const lines = buildCmaProgressLines(events);
  if (lines.length === 0) return null;

  return (
    <div
      className="mb-2 px-2 py-1.5 space-y-1"
      data-testid="cma-progress-lines"
      aria-live="polite"
    >
      {lines.map((line) => (
        <div key={line.key} className="flex items-center gap-2 text-xs text-text-tertiary">
          {line.status === 'running' ? (
            <Loader2 className="w-3.5 h-3.5 shrink-0 animate-spin text-primary/70" />
          ) : line.degraded ? (
            <AlertTriangle className="w-3.5 h-3.5 shrink-0 text-amber-500" />
          ) : (
            <CheckCircle2 className="w-3.5 h-3.5 shrink-0 text-emerald-500 thinking-check-appear" />
          )}
          <span className="truncate">{line.title}</span>
        </div>
      ))}
    </div>
  );
}
